import { Outlet, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { useTenantLogo } from '@/hooks/useTenantLogo';
import { useTenantName } from '@/hooks/useTenantName';
import { useTenantHostCache } from '@/features/tenant-config/hooks/useTenantByHost';
import { Building2, Shield, BarChart3, Cog, CheckCircle } from 'lucide-react';
import {
  DEFAULT_HERO_IMAGES,
  HERO_CAROUSEL_NAME,
  HERO_SLOT_SECONDS,
} from '@/lib/theme/constants';

/**
 * Generated per count so every slot is covered: a fixed four-slot animation
 * left a workspace with two images staring at a blank panel for half the loop.
 */
function buildCarouselKeyframes(count: number): string {
  const slot = 100 / count;
  const fade = slot * 0.16;
  return `@keyframes ${HERO_CAROUSEL_NAME}${count} {
    0% { opacity: 0; }
    ${fade.toFixed(2)}% { opacity: 1; }
    ${slot.toFixed(2)}% { opacity: 1; }
    ${(slot + fade).toFixed(2)}% { opacity: 0; }
    100% { opacity: 0; }
  }`;
}

export function AuthLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword);
  // The hero sits on a photo under a dark scrim, so it never follows the theme.
  const tenantName = useTenantName();
  const configured = useTenantHostCache()?.branding?.['branding.heroImages'];
  const heroImages = configured?.length ? configured : DEFAULT_HERO_IMAGES;
  const heroLogo = useTenantLogo('dark');
  const formLogo = useTenantLogo();

  if (isAuthenticated && !mustChangePassword) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <div className="flex min-h-screen">
      {/* Left Panel — Branding + Background */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Crossfade, sized to however many images there are. The keyframes are
            generated rather than fixed: hardcoding four slots left a workspace
            with two images showing a blank panel for half the loop. */}
        {heroImages.length > 1 && (
          <style>{buildCarouselKeyframes(heroImages.length)}</style>
        )}

        {heroImages.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              opacity: i === 0 ? 1 : 0,
              ...(heroImages.length > 1
                ? {
                    animation: `${HERO_CAROUSEL_NAME}${heroImages.length} ${
                      heroImages.length * HERO_SLOT_SECONDS
                    }s ${i * HERO_SLOT_SECONDS}s infinite`,
                  }
                : {}),
            }}
          />
        ))}

        {/* Dark overlay with brand tint — heavier at bottom for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/50 to-[#0A0A0A]/60" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo — big and prominent */}
          <div className="overflow-hidden">
            <img
              src={heroLogo.src}
              alt={heroLogo.alt}
              onError={heroLogo.onError}
              className={
                heroLogo.isCustom
                  ? 'h-16 w-auto max-w-[14rem] object-contain'
                  : 'h-72 -ml-16 -my-24 object-contain'
              }
            />
          </div>

          {/* Main tagline — larger text */}
          <div className="max-w-xl -mt-4">
            <h1 className="text-5xl font-extrabold text-white leading-[1.15] mb-5 tracking-tight">
              Mining Operations<br />Management System
            </h1>
            <p className="text-white/70 text-xl leading-relaxed mb-6">
              The complete digital platform for artisanal and small-scale mining operations in Africa.
            </p>
            <div className="space-y-3">
              {[
                'End-to-end mineral traceability from extraction to export',
                'Compliance, safety, and environmental monitoring',
                'Workforce management with biometric attendance',
                'Real-time production analytics and reporting',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle className="size-4 text-amber-200 mt-0.5 shrink-0" />
                  <p className="text-white/50 text-sm leading-snug">{item}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats bar */}
          <div className="flex gap-6">
            {[
              { icon: Building2, value: '50+', label: 'Tenants Served' },
              { icon: Cog, value: '9', label: 'Services' },
              { icon: Shield, value: '100%', label: 'Compliance' },
              { icon: BarChart3, value: 'Real-time', label: 'Analytics' },
            ].map(({ icon: Icon, value, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm border border-white/5">
                  <Icon className="size-5 text-white/60" />
                </div>
                <div>
                  <p className="text-xl font-bold text-white">{value}</p>
                  <p className="text-[11px] text-white/40">{label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Login Form */}
      <div className="flex w-full lg:w-[45%] flex-col items-center justify-center bg-background p-6 sm:p-10">
        {/* Mobile logo (hidden on desktop) */}
        <div className="lg:hidden mb-8">
          <img
            src={formLogo.src}
            alt={formLogo.alt}
            onError={formLogo.onError}
            className={
              formLogo.isCustom
                ? 'h-14 w-auto max-w-[12rem] object-contain'
                : 'h-24 -my-6 object-contain'
            }
          />
        </div>

        <div className="w-full max-w-sm">
          <Outlet />
        </div>

        {/* Footer */}
        <p className="mt-10 text-xs text-text-body text-center">
          &copy; {new Date().getFullYear()} {tenantName}. All rights reserved.
        </p>
      </div>
    </div>
  );
}
